/**
 * Entity "variant OR blocks": one filter chip holds a primary `text` plus alias
 * `variants` (spellings, acronyms, synonyms of the same concept). The block
 * matches when ANY of its forms matches; required/optional/exclude and scope
 * apply to the block as a whole. These rules mirror the backend's
 * normalization so what the UI writes is what the API persists.
 */

/** Most aliases one block may carry; the API rejects more. */
export const MAX_ENTITY_VARIANTS = 10;
/** Longest single alias, in characters; the API rejects longer. */
export const MAX_ENTITY_VARIANT_LEN = 200;

/** Filter fields whose chips may carry variants. Enum-like fields never do. */
export const VARIANT_FIELDS = [
  "skills",
  "job_titles",
  "past_job_titles",
  "companies",
  "universities",
  "industries",
  "majors",
  "certification_names",
  "certification_vendors",
  "company_tags",
] as const;

export type VariantField = (typeof VARIANT_FIELDS)[number];

export type VariantBlock = {
  text?: string | null;
  variants?: string[] | null;
};

function collapseWhitespace(value: string): string {
  return value.replace(/\s+/g, " ").trim();
}

/**
 * Collapses whitespace, drops blanks, drops forms equal to `text`
 * (case-insensitively) and case-insensitive repeats, keeping first-seen order.
 */
export function normalizeVariants(
  text: string | null | undefined,
  variants: readonly string[] | null | undefined,
): string[] {
  const seen = new Set([collapseWhitespace(text ?? "").toLowerCase()]);
  const out: string[] = [];
  for (const raw of variants ?? []) {
    const variant = collapseWhitespace(raw);
    const key = variant.toLowerCase();
    if (!variant || seen.has(key)) continue;
    seen.add(key);
    out.push(variant);
  }
  return out;
}

/** Returns `item` with normalized `variants`, dropping the key when none remain. */
export function withVariants<T extends VariantBlock>(
  item: T,
  variants: readonly string[] | null | undefined,
): T {
  const next = normalizeVariants(item.text, variants);
  const rest = { ...item };
  delete rest.variants;
  return next.length > 0 ? { ...rest, variants: next } : rest;
}

export type AddVariantRejection = "empty" | "too_long" | "limit" | "duplicate";

export type AddVariantResult<T> =
  | { ok: true; item: T }
  | { ok: false; reason: AddVariantRejection };

/** Adds one alias to a block, enforcing the API's limits and dedupe rules. */
export function addVariant<T extends VariantBlock>(
  item: T,
  raw: string,
): AddVariantResult<T> {
  const variant = collapseWhitespace(raw);
  if (!variant) return { ok: false, reason: "empty" };
  if (variant.length > MAX_ENTITY_VARIANT_LEN) return { ok: false, reason: "too_long" };
  const current = normalizeVariants(item.text, item.variants);
  if (current.length >= MAX_ENTITY_VARIANTS) return { ok: false, reason: "limit" };
  const next = normalizeVariants(item.text, [...current, variant]);
  if (next.length === current.length) return { ok: false, reason: "duplicate" };
  return { ok: true, item: withVariants(item, next) };
}

export function removeVariant<T extends VariantBlock>(item: T, index: number): T {
  return withVariants(
    item,
    (item.variants ?? []).filter((_, i) => i !== index),
  );
}

/** Every surface form of a block: the primary text first, then its aliases. */
export function entityForms(item: VariantBlock): string[] {
  const text = collapseWhitespace(item.text ?? "");
  return [...(text ? [text] : []), ...normalizeVariants(text, item.variants)];
}

/** Human-facing rendering of a block, e.g. `React or React.js`. */
export function formatEntityForms(item: VariantBlock): string {
  return entityForms(item).join(" or ");
}

/**
 * True when `value` case-insensitively equals the text or any alias of a block
 * already in `items` — adding it as a new chip would duplicate that block.
 */
export function hasEntityForm(items: readonly VariantBlock[], value: string): boolean {
  const key = collapseWhitespace(value).toLowerCase();
  if (!key) return false;
  return items.some((item) => entityForms(item).some((form) => form.toLowerCase() === key));
}
